// src/components/TenantUserManagement.js - SuperAdmin user management for a specific tenant
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useAuth } from '../../contexts/AuthContext';
import { useRoles } from '../../hooks/useRoles';
import { useApiCall } from '../../hooks/useApiCall';
import apiService from '../../services/apiService';
import SuperAdminUserCard from './SuperAdminUserCard';
import '../../styles/globals.css';
import './UserManagement.css';

const TenantUserManagement = ({ tenant, onBack }) => {
  const { user } = useAuth();
  const { userProfile } = useRoles();
  const [users, setUsers] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState(''); 
  const [activeSearchQuery, setActiveSearchQuery] = useState(''); 
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 25,
    total: 0,
    totalPages: 0,
    hasMore: false
  });

  const { loading, error, makeRequest } = useApiCall();

  // Roles a SuperAdmin can assign from this screen. Not filtered by the
  // tenant_content feature flag - this view spans tenants and is already
  // SuperAdmin-only, unlike UserManagement.js's tenant-scoped role list.
  const availableRoles = [
    { id: 'User', name: 'User', description: 'Standard user with limited access.' },
    { id: 'ContentManager', name: 'Content Manager', description: 'Can manage organization-specific content.' },
    { id: 'Coach', name: 'Coach', description: 'Reporting access limited to the cohorts they are assigned to coach.' },
    { id: 'Admin', name: 'Admin', description: 'Full access to all features and settings.' },
    { id: 'SuperContentManager', name: 'Super Content Manager', description: 'Content management and QA access across all tenants.' }
  ];

  // Reset to page 1 when search or filter changes
  useEffect(() => {
    setPagination(prev => ({ ...prev, page: 1 }));
  }, [activeSearchQuery, statusFilter]);

  useEffect(() => {
    if (tenant?.id) {
      loadUsers();
    }
  }, [tenant?.id, statusFilter, activeSearchQuery, pagination.page]);

  const loadUsers = async (page = pagination.page) => {
    if (!tenant?.id) return;

    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: pagination.limit.toString(),
        status: statusFilter,
        ...(activeSearchQuery && { search: activeSearchQuery })
      });

      const data = await makeRequest(
        () => apiService.get(`/api/v1/tenants/${tenant.id}/users?${params}`, user),
        'Loading users...'
      );

      if (data.success) {
        setUsers(data.data.users || []);
        setPagination(data.data.pagination || pagination);
      }
    } catch (err) {
      console.error('Error loading users:', err);
    }
  };

  const suspendUser = async (userId) => {
    try {
      const data = await makeRequest(
        () => apiService.put(`/api/v1/tenants/${tenant.id}/users/${userId}`, { status: 'suspended' }, user),
        'Suspending user...'
      );

      if (data.success) {
        await loadUsers();
        return data;
      }
    } catch (err) {
      console.error('Error suspending user:', err);
      throw err;
    }
  };

  const reactivateUser = async (userId) => {
    try {
      const data = await makeRequest(
        () => apiService.put(`/api/v1/tenants/${tenant.id}/users/${userId}`, { status: 'active' }, user),
        'Reactivating user...'
      );

      if (data.success) {
        await loadUsers();
        return data;
      }
    } catch (err) {
      console.error('Error reactivating user:', err);
      throw err;
    }
  };

  const updateUserRoles = async (userId, roles) => {
    // Don't use makeRequest here - the modal handles its own errors
    const data = await apiService.put(`/api/v1/tenants/${tenant.id}/users/${userId}`, { roles }, user);

    if (data.success) {
      await loadUsers(); // Refresh list to reflect the new roles
      return data;
    }

    // If not successful, throw error to be caught by modal
    throw new Error(data.message || 'Failed to update roles');
  };

  const migrateUser = async (userId, targetTenantId, reason) => {
    // Don't use makeRequest here - the modal handles its own errors
    const requestBody = {
      targetTenantId,
      ...(reason && { reason })
    };

    const data = await apiService.post(`/api/v1/tenants/${tenant.id}/users/${userId}/migrate`, requestBody, user);
    
    if (data.success) {
      await loadUsers(); // Refresh list - user should be gone
      return data;
    }
    
    // If not successful, throw error to be caught by modal
    throw new Error(data.message || 'Failed to migrate user');
  };

  const deleteUser = async (userId) => {
    // Don't use makeRequest here - the modal handles its own errors
    // Using makeRequest would set the global error state and show "Error loading users" on screen
    const data = await apiService.delete(`/api/v1/users/${userId}`, user);
    
    if (data.success) {
      await loadUsers(); // Refresh the user list
      return data;
    }
    
    // If not successful, throw error to be caught by modal
    throw new Error(data.message || 'Failed to delete user');
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'active': return 'status-pill active';
      case 'invited': return 'status-pill invited';
      case 'suspended': return 'status-pill suspended';
      default: return 'status-pill archived';
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setActiveSearchQuery(searchQuery);
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    setActiveSearchQuery('');
  };

  return (
    <div>
      {/* Header with Tenant Context */}
      <div className="mb-6">
        <div className="flex gap-2 mb-2" style={{ alignItems: 'center' }}>
          <button 
            onClick={onBack}
            className="btn btn-secondary btn-sm"
            disabled={loading}
          >
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Tenants
          </button>
        </div>
        
        <div className="flex flex-between">
          <div>
            <h2 className="text-2xl font-semibold text-gray-800">Manage Users</h2>
            <div className="flex gap-2 mt-1" style={{ alignItems: 'center' }}>
              <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
              <span className="text-lg text-gray-600">{tenant.name}</span>
              <span className="text-sm text-gray-400">({tenant.slug})</span>
            </div>
          </div>
          
          <button 
            onClick={() => loadUsers()}
            className="btn btn-secondary"
            disabled={loading}
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Refresh
          </button>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="filter-bar">
        <div className="filter-row">
          {/* Status Filter */}
          <div className="filter-group">
            <label className="form-label mb-2">Filter by Status</label>
            <select 
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="form-input"
              style={{ minWidth: '120px' }}
            >
              <option value="all">All Users</option>
              <option value="active">Active</option>
              <option value="invited">Invited</option>
              <option value="suspended">Suspended</option>
            </select>
          </div>

          {/* Search */}
          <div className="search-group">
            <label className="form-label mb-2">Search</label>
            <form onSubmit={handleSearch} className="flex gap-2">
              <input
                type="text"
                placeholder="Search by name or email, then press Enter..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="form-input flex-1"
              />
              <button 
                type="submit" 
                className="btn btn-primary"
                disabled={loading}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </button>
              {activeSearchQuery && (
                <button 
                  type="button"
                  onClick={handleClearSearch}
                  className="btn btn-secondary"
                  disabled={loading}
                >
                  Clear
                </button>
              )}
            </form>
          </div>
        </div>

        {/* Results Summary */}
        {(activeSearchQuery || statusFilter !== 'all') && (
          <div className="text-sm text-gray-600 mt-2">
            {pagination.total} user{pagination.total !== 1 ? 's' : ''} found
            {activeSearchQuery && ` matching "${activeSearchQuery}"`}
            {statusFilter !== 'all' && ` with status: ${statusFilter}`}
          </div>
        )}
      </div>

      {/* Environment Warning for Migration */}
      {process.env.NODE_ENV === 'production' && (
        <div className="alert alert-warning mb-4">
          <svg className="w-5 h-5 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <span>User migration is disabled in production environments.</span>
        </div>
      )}

      {/* Loading State */}
      {loading && users.length === 0 ? (
        <div className="text-center py-8">
          <div className="loading-dots mx-auto mb-3">
            <div className="loading-dot"></div>
            <div className="loading-dot"></div>
            <div className="loading-dot"></div>
          </div>
          <p className="text-gray-600">Loading users...</p>
        </div>
      ) : error ? (
        /* Error State */
        <div className="alert alert-error">
          <div>
            <strong>Error loading users:</strong> {error}
            <br />
            <small>Check the browser console for more details.</small>
          </div>
          <button 
            onClick={() => loadUsers()}
            className="btn btn-secondary btn-sm ml-3"
          >
            Try Again
          </button>
        </div>
      ) : users.length === 0 ? (
        /* Empty State */
        <div className="card text-center py-12">
          <div className="icon-container gray">
            <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-700 mb-2">
            No users found
          </h3>
          <p className="text-gray-500">
            {activeSearchQuery || statusFilter !== 'all'
              ? 'Try adjusting your search or filters.' 
              : 'This tenant has no users yet.'}
          </p>
        </div>
      ) : (
        /* User List */
        <>
          <div className="space-y-4">
            {users.map(u => (
              <SuperAdminUserCard
                key={u.id}
                user={u}
                tenant={tenant}
                currentUserId={userProfile?.id}
                onSuspend={suspendUser}
                onReactivate={reactivateUser}
                onMigrate={migrateUser}
                onDelete={deleteUser}
                onUpdateRoles={updateUserRoles}
                availableRoles={availableRoles}
                isLoading={loading}
                getStatusBadgeClass={getStatusBadgeClass}
                formatDate={formatDate}
              />
            ))}
          </div>

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="flex justify-center gap-2 mt-6">
              <button
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                disabled={pagination.page === 1 || loading}
                className="btn btn-secondary btn-sm"
              >
                Previous
              </button>
              <span className="px-4 py-2 text-sm text-gray-600">
                Page {pagination.page} of {pagination.totalPages}
              </span>
              <button
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                disabled={pagination.page >= pagination.totalPages || loading}
                className="btn btn-secondary btn-sm"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

TenantUserManagement.propTypes = {
  tenant: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    name: PropTypes.string.isRequired,
    slug: PropTypes.string.isRequired
  }).isRequired,
  onBack: PropTypes.func.isRequired
};

export default TenantUserManagement;